import { ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { TreoNavigationItem } from 'tsui/@treo';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

import { LegacyTasksTabService } from '../services/tasks.service';
import { TaskList } from '../tasks.types';
import { AuthService } from 'app/core';
import { FeatureFlag, Module, NewPermission, Permission, ReservedRole } from 'app/core/enums';
import { RelatedToType } from 'app/shared/enums';
import { User } from 'app/core/models';
import { TSTreeAction, TSTreeNode } from 'tsui';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ObjectsManagerService } from 'app/modules/settings/objects-manager/services/objects-manager.service';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { AccountService } from 'app/core';
import { Store } from '@ngxs/store';
import { PERMISSION_DENY_LABEL } from '@shared/literals';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { CreateTaskListComponent } from '@tasks/task-shell/create-task-list/create-task-list.component';

@Component({
  selector: 'tasks-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LegacyTasksSidebarComponent extends OnDestroyMixin {
  taskLists: TaskList[];
  menuData: TreoNavigationItem[];
  isTaskCollaborator: boolean;
  tsTreeTaskLists: TSTreeNode[] = [];
  tsTreeActions: TSTreeAction[] = [];

  private _taskListsMenuData: TreoNavigationItem[];
  private _otherMenuData: TreoNavigationItem[];
  customObject: CustomObject;

  private get relatedToType(): RelatedToType | undefined {
    return this._activatedRoute.snapshot.data?.relatedToType;
  }

  get module(): string | undefined {
    return this._activatedRoute.snapshot.data?.module;
  }

  constructor(
    private readonly _tasksService: LegacyTasksTabService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly authService: AuthService,
    private readonly _matDialog: MatDialog,
    private readonly accountService: AccountService,
    private readonly objectsManagerService: ObjectsManagerService,
    private readonly store: Store,
  ) {
    super();

    // Set the private defaults
    this._taskListsMenuData = [];
    this._otherMenuData = [];

    // Set the defaults
    this.menuData = [];
  }

  ngOnInit(): void {
    const initialData = this._activatedRoute.snapshot.data.initialData;

    this._tasksService.taskLists$.pipe(untilComponentDestroyed(this)).subscribe((taskLists: TaskList[]) => {
      this.taskLists = taskLists.sort((a, b) => a.rank - b.rank);
      this._generateTaskListsMenuLinks();
      this.changeDetectorRef.markForCheck();
    });

    this.isTaskCollaborator = (initialData.user as User).roles.some((role) => role.name === ReservedRole.Collaborator);
    this._generateTaskListsMenuLinks();

    // Generate other menu links
    this._generateOtherMenuLinks();

    this.checkRedirectToFirstPipeline(this._activatedRoute);

    this._router.events.pipe(untilComponentDestroyed(this)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        //default route if navigate
        this.checkRedirectToFirstPipeline(this._activatedRoute);
      }
    });

    if (this._tasksService.moduleBaseUrl?.includes('custom_objects')) {
      this.objectsManagerService
        .getById(this.accountId, Number(this._tasksService.baseModuleId))
        .pipe(untilComponentDestroyed(this))
        .subscribe((object) => {
          this.customObject = object;
        });
    }
  }

  /**
   * Check if we should redirect to first pipeline
   */
  private checkRedirectToFirstPipeline(route: ActivatedRoute): void {
    if (!route.snapshot.firstChild && this.taskLists.length > 0) {
      this._router.navigate(['./', this.taskLists[0].id], { relativeTo: route });
    }
  }

  /**
   * Generate menus for folders
   *
   * @private
   */
  private _generateTaskListsMenuLinks(): void {
    // Reset the folders menu data
    this._taskListsMenuData = [];
    this.tsTreeTaskLists = [];
    const url = this.getTaskListBaseUrl();

    // Iterate through the folders
    this.taskLists.forEach((taskList) => {
      this.tsTreeTaskLists.push({
        id: taskList.id.toString(),
        name: taskList.name,
        icon: 'list_alt',
        link: `${url}/${taskList.id}`,
        badge: {
          value: taskList.complete_task_count + '/' + taskList.task_count,
          type: 'warning',
        },
      });

      // Generate menu item for the folder
      const menuItem: TreoNavigationItem = {
        id: taskList.id.toString(),
        title: taskList.name,
        icon: 'list_alt',
        type: 'basic',
        link: './' + taskList.id,
      };

      menuItem['badge'] = {
        title: taskList.complete_task_count + '/' + taskList.task_count,
        style: 'simple',
      };

      // Push the menu item to the folders menu data
      this._taskListsMenuData.push(menuItem);
    });

    if (!this.isTaskCollaborator) {
      const taskListGlobalSettings = this.authService.hasPermission(Permission.TASK_GLOBAL_SETTINGS);
      const projectTaskSettings = this.authService.hasPermission(Permission.DEAL_TASK_SETTINGS);
      const propertiesTaskSettings = this.authService.hasPermission(Permission.PROPERTY_TASKS_TAB);

      const menuItem: TreoNavigationItem = {
        title: 'Add Task List',
        type: 'basic',
        icon: 'add_circle_outline',
        link: './settings',
        hidden: () => {
          return !this.showTaskSettings(taskListGlobalSettings, projectTaskSettings, propertiesTaskSettings);
        },
      };
      this._taskListsMenuData.push(menuItem);

      if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
        if (this.module === Module.GLOBAL_TASKS) {
          this.tsTreeActions = [];

          if (this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_CREATE))) {
            this.tsTreeActions = [
              ...this.tsTreeActions,
              {
                key: 'new-task-list',
                label: 'Add Task List',
                icon: 'add_circle_outline',
              },
            ];
          }

          if (this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_EDIT))) {
            this.tsTreeActions = [
              ...this.tsTreeActions,
              {
                key: 'manage-task-dependencies',
                label: 'Settings',
                icon: 'settings',
              },
            ];
          }
        } else {
          this.tsTreeActions = [];

          if (this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_CREATE))) {
            this.tsTreeActions = [
              ...this.tsTreeActions,
              {
                key: 'new-task-list',
                label: 'Add Task List',
                icon: 'add_circle_outline',
              },
            ];
          }

          if (this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT))) {
            this.tsTreeActions = [
              ...this.tsTreeActions,
              {
                key: 'manage-task-dependencies',
                label: 'Settings',
                icon: 'settings',
              },
            ];
          }
        }
      } else {
        if (this.showTaskSettings(taskListGlobalSettings, projectTaskSettings, propertiesTaskSettings)) {
          this.tsTreeActions = [
            {
              key: 'new-task-list',
              label: 'Add Task List',
              icon: 'add_circle_outline',
            },
            {
              key: 'manage-task-dependencies',
              label: 'Settings',
              icon: 'settings',
            },
          ];
        }
      }
    }

    // Update the menu data
    this._updateMenuData();
  }

  private showTaskSettings(taskListGlobalSettings, projectTaskSettings, propertiesTaskSettings): boolean {
    if (this.module === 'DEALS') {
      return projectTaskSettings;
    } else if (this.module === 'PROPERTIES') {
      return propertiesTaskSettings;
    } else {
      return taskListGlobalSettings;
    }
  }

  /**
   * Generate other menus
   *
   * @private
   */
  private _generateOtherMenuLinks(): void {
    // Settings menu
    this._otherMenuData.push({
      title: 'Settings',
      type: 'basic',
      icon: 'settings',
      link: './settings',
      hidden: () => {
        if (this.relatedToType === RelatedToType.Project) {
          return !this.authService.hasPermission(Permission.DEAL_TASK_SETTINGS);
        }

        return !this.authService.hasPermission(Permission.TASK_GLOBAL_SETTINGS);
      },
    });

    // Update the menu data
    this._updateMenuData();
  }

  /**
   * Update the menu data
   *
   * @private
   */
  private _updateMenuData(): void {
    if (this.isTaskCollaborator) {
      this.menuData = [
        {
          title: 'TASK LISTS',
          type: 'group',
          children: [...this._taskListsMenuData],
        },
        {
          type: 'divider',
        },
      ];
    } else {
      this.menuData = [
        {
          title: 'TASK LISTS',
          type: 'group',
          children: [...this._taskListsMenuData],
        },
        {
          type: 'divider',
        },
        ...this._otherMenuData,
      ];
    }
  }

  onActionClicked(action: TSTreeAction): void {
    const actionMap = {
      'new-task-list': () => this.createNewTaskList(),
      'manage-task-dependencies': () => this._router.navigate(['settings'], { relativeTo: this._activatedRoute }),
    };

    actionMap[action.key] && actionMap[action.key]();
  }

  createNewTaskList(): void {
    let applicableType: string = this.store.selectSnapshot((state) => state.taskState.relatedToType as RelatedToType);

    if (applicableType === RelatedToType.CustomObject) {
      applicableType = this.customObject?.name;
    }

    if (this.customObject?.name) {
      applicableType = this.customObject.name;
    }

    this._matDialog.open(CreateTaskListComponent, { data: { applicableType } });
  }

  private get accountId(): number {
    return Number(this.accountService.getCurrentAccount().id);
  }

  private getTaskListBaseUrl(): string {
    let url = this._router.url;
    let splittedUrl = this._router.url.split('/');
    const isGlobalTask = url.includes('/global');

    const lastIndex = (isGlobalTask ? splittedUrl.lastIndexOf('global') : splittedUrl.lastIndexOf('tasks')) + 1;
    return splittedUrl.slice(0, lastIndex).join('/');
  }
}
